import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { Router } from '@angular/router'
import { Table } from 'primeng/table'
import { Subscription, combineLatest } from 'rxjs'
import { finalize, first } from 'rxjs/operators'
import { AccountSubjectDto, AuthenticatedPrincipalDto, BillingNotificationsDto } from 'src/app/services/dtos'
import { subscriptionActive } from 'src/utils/rxjs-utils'
import { handleArpioError } from 'src/utils/handle-error'
import { AccountService } from '../../services/account.service'
import { UiService } from 'src/app/services/ui.service'
import { ApiService } from 'src/app/services/api.service'
import { BaseError } from '../../../../utils/errors'
import { AccessLevelService, AccountPermission } from 'src/app/services/access-level.service'

// One row in the billing-notification subscriber roster.
interface SubscriberRow {
  accountSubjectId: string
  name: string
  email: string
  isYou: boolean
  subscribed: boolean
}

@Component({
  selector: 'app-account-notifications',
  templateUrl: './account-notifications.component.html',
  styleUrls: ['./account-notifications.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AccountNotificationsComponent implements OnInit, OnDestroy {
  isLoading = true
  permissionsLoaded = false
  private hasUpdateAccount = false

  subscribers: SubscriberRow[] = []
  isFilterApplied = false

  @ViewChild('subscribersTable') subscribersTable: Table
  @ViewChild('filterInput') filterInput: ElementRef

  private loadSubscription: Subscription = null
  private toggleSubscription: Subscription = null
  private permissionsSubscription: Subscription = null

  get rbacEnabled(): boolean {
    return this.accountService.rbacEnabled
  }

  constructor(
    private uiService: UiService,
    private apiService: ApiService,
    public accountService: AccountService,
    private accessLevelService: AccessLevelService,
    private router: Router
  ) {}

  ngOnInit() {
    this.permissionsSubscription = this.accessLevelService
      .hasAccountPermission(AccountPermission.UPDATE_ACCOUNT)
      .pipe(first())
      .subscribe((hasUpdateAccount) => {
        this.hasUpdateAccount = hasUpdateAccount
        this.permissionsLoaded = true

        // PRD: only account admins see this page — the same permission subscribe/unsubscribe need.
        if (!hasUpdateAccount && this.rbacEnabled) {
          this.uiService.announceError(
            new BaseError('Insufficient permissions', 'You need the Account Admin role to manage notifications.')
          )
          this.router.navigate(['/', this.accountService.accountId])
          return
        }

        // Capability-gated: the tab is hidden, and a typed URL lands on not-found.
        if (!this.accountService.billingNotificationsEnabled) {
          this.router.navigateByUrl('/not-found', { skipLocationChange: true })
          return
        }

        this.loadSubscribers()
      })
  }

  // Builds the roster from the account's members, marks who is subscribed from the
  // backend's email list, and appends subscribed emails that no longer match a member
  // (e.g. a removed admin) so the count and the "no one is subscribed" notice stay truthful.
  private loadSubscribers() {
    const accountId = this.accountService.accountId
    this.loadSubscription = combineLatest([
      this.apiService.getAuthenticatedPrincipal().pipe(first()),
      this.apiService.listAccountSubjects(accountId).pipe(first()),
      this.apiService.getBillingNotifications(accountId).pipe(first()),
    ]).subscribe({
      next: ([principal, subjects, notifications]) => {
        const subscribed = new Set(notifications.subscribers.map((email) => email.toLowerCase()))
        const rows = subjects.map((subject) => this.toSubscriberRow(subject, principal, notifications, subscribed))
        const memberEmails = new Set(rows.map((row) => row.email.toLowerCase()))
        const orphans = notifications.subscribers
          .filter((email) => !memberEmails.has(email.toLowerCase()))
          .map((email) => ({ accountSubjectId: email, name: email, email, isYou: false, subscribed: true }))
        this.subscribers = [...rows, ...orphans].sort(this.bySelfThenName)
        this.isLoading = false
      },
      error: (error) => {
        this.isLoading = false
        this.uiService.announceApiError(error, 'Failed to load billing notification subscribers')
      },
    })
  }

  private toSubscriberRow(
    subject: AccountSubjectDto,
    principal: AuthenticatedPrincipalDto,
    notifications: BillingNotificationsDto,
    subscribedEmails: Set<string>
  ): SubscriberRow {
    const name = subject.name || subject.email || 'Unknown user'
    const email = subject.email || ''
    // Match the signed-in principal to their account subject the same way the
    // profile page does (covers both native and SSO identities).
    const isYou =
      subject.subjectId === principal.subjectId ||
      !!principal.effectiveAccountSubjectIds?.includes(subject.accountSubjectId)
    return {
      accountSubjectId: subject.accountSubjectId,
      name,
      email,
      isYou,
      // The backend already resolved the caller's own state; other rows match by email.
      subscribed: isYou ? notifications.subscribed : subscribedEmails.has(email.toLowerCase()),
    }
  }

  // Your own row sorts first, then alphabetically by name.
  private bySelfThenName = (a: SubscriberRow, b: SubscriberRow): number => {
    if (a.isYou !== b.isYou) {
      return a.isYou ? -1 : 1
    }
    return a.name.localeCompare(b.name)
  }

  subscriberTrackBy(_index: number, row: SubscriberRow) {
    return row.accountSubjectId
  }

  // Search wiring mirrors the other settings tables (PrimeNG global filter).
  filterGlobal(event: Event) {
    const value = (event.target as HTMLInputElement).value.toLowerCase().trim()
    if (!value) {
      this.subscribersTable.clear()
      this.isFilterApplied = false
      return
    }
    this.subscribersTable.filterGlobal(value, 'contains')
    this.isFilterApplied = true
  }

  clearInput() {
    this.filterInput.nativeElement.value = ''
    this.isFilterApplied = false
    this.subscribersTable.clear()
  }

  // Self-service subscribe/unsubscribe for the signed-in admin. The switch is already
  // flipped by ngModel, so on failure we flip it back and let the toast explain (e.g. the
  // backend's 422 when removing the last subscriber).
  onToggleSubscription(row: SubscriberRow) {
    const accountId = this.accountService.accountId
    const request$ = row.subscribed
      ? this.apiService.subscribeBillingNotifications(accountId)
      : this.apiService.unsubscribeBillingNotifications(accountId)
    this.toggleSubscription = request$.pipe(finalize(() => (this.toggleSubscription = null))).subscribe({
      next: () =>
        this.uiService.announceCopied(
          row.subscribed ? 'Subscribed to billing notifications' : 'Unsubscribed from billing notifications'
        ),
      error: (error) => {
        row.subscribed = !row.subscribed
        handleArpioError(window.location.href, this.apiService, this.uiService)(error)
      },
    })
  }

  get subscribedCount(): number {
    return this.subscribers.filter((row) => row.subscribed).length
  }

  get userManagementLink(): string[] {
    return ['/', this.accountService.accountId, 'settings', 'access-management']
  }

  get canViewAccountSettings(): boolean {
    return this.hasUpdateAccount
  }

  get isContentLoading(): boolean {
    return !this.permissionsLoaded || this.isLoading
  }

  ngOnDestroy() {
    if (subscriptionActive(this.permissionsSubscription)) {
      this.permissionsSubscription.unsubscribe()
      this.permissionsSubscription = null
    }
    if (subscriptionActive(this.loadSubscription)) {
      this.loadSubscription.unsubscribe()
      this.loadSubscription = null
    }
    if (subscriptionActive(this.toggleSubscription)) {
      this.toggleSubscription.unsubscribe()
      this.toggleSubscription = null
    }
  }
}
